// piano-frontend/src/pages/admin/OperationsSettings.js
// 관리자 운영 설정 (PC 전용 섹션).
// 탭: 운영 시간 설정(기본) / 운영 시간 설정(예외) / 선생님 스케줄 설정(기본) / 선생님 스케줄 설정(블록).
// 예외 탭은 휴일·레슨 예외·연습실 예외·변동 스케줄 4개 레이어를 한 달력에 표시하고,
// 날짜를 클릭하면 우측 패널에서 유효 상태 확인과 예외 추가/수정/삭제를 한 곳에서 처리한다.
// 기존 다이얼로그·API는 그대로 두고 같은 API를 재사용한다 (추가형).
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, Stack, Chip, IconButton, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch, Checkbox,
  Paper, CircularProgress, Tooltip
} from "@mui/material";
import { ChevronLeft, ChevronRight, Delete, Edit, Add, EventBusy, Close, Refresh } from "@mui/icons-material";
import dayjs from "dayjs";
import api from "../../api/axios";
import { useDarkMode } from "../../App";
import { useDialog } from "../../contexts/DialogProvider";
import DateImpactPreview from "../../components/DateImpactPreview";
import OperationsBasicHours from "./OperationsBasicHours";
import OperationsTeacherSchedule from "./OperationsTeacherSchedule";
import OperationsBlockSchedule from "./OperationsBlockSchedule";

const TIME_OPTIONS = (() => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      options.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return options;
})();

// 레이어 색 (라이트/다크)
const LAYER_COLORS = {
  holiday: { light: "#c62828", dark: "#ef9a9a" },
  lesson: { light: "#e58a00", dark: "#ffcc80" },
  practice: { light: "#1565c0", dark: "#90caf9" },
  teacher: { light: "#6a4fc4", dark: "#b39ddb" }
};

const DAY_HEAD = ["일", "월", "화", "수", "목", "금", "토"];

function LayerDot({ color }) {
  return <Box component="span" sx={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", bgcolor: color, mx: 0.2 }} />;
}

function TimeSelect({ label, value, onChange, minWidth = 96 }) {
  return (
    <FormControl size="small" sx={{ minWidth }}>
      <InputLabel shrink>{label}</InputLabel>
      <Select value={value} label={label} onChange={(e) => onChange(e.target.value)} notched>
        {TIME_OPTIONS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

export default function OperationsSettings() {
  const { darkMode } = useDarkMode();
  const { confirm, toast, alert } = useDialog();
  const layerColor = (key) => LAYER_COLORS[key][darkMode ? "dark" : "light"];

  const [subTab, setSubTab] = useState(0);
  const [month, setMonth] = useState(dayjs().startOf("month"));
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));

  const [teachers, setTeachers] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [lessonOverrides, setLessonOverrides] = useState([]);
  const [practiceOverrides, setPracticeOverrides] = useState([]);
  const [teacherOverrides, setTeacherOverrides] = useState([]);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // activeForm: null | 'lesson' | 'practice' | 'teacher' | 'holiday'
  const [activeForm, setActiveForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lessonForm, setLessonForm] = useState({ is_open: true, start_time: "12:00", end_time: "18:00", reason: "" });
  const [practiceForm, setPracticeForm] = useState({ is_open: true, start_time: "12:00", end_time: "18:00", apply_unavailable_times: true, reason: "" });
  const [teacherForm, setTeacherForm] = useState({ teacher_id: "", type: "OFF", work_start: "09:00", work_end: "18:00", reason: "" });
  const [holidayForm, setHolidayForm] = useState({ refund_tickets: true, reason: "" });

  const fetchTeachers = useCallback(async () => {
    try {
      const res = await api.get("/teachers");
      setTeachers(res.data);
      return res.data;
    } catch {
      setTeachers([]);
      return [];
    }
  }, []);

  const fetchMonthData = useCallback(async (teacherList) => {
    const start = month.format("YYYY-MM-DD");
    const end = month.endOf("month").format("YYYY-MM-DD");
    const list = teacherList || teachers;
    try {
      const [holidayRes, lessonRes, practiceRes, teacherOvLists] = await Promise.all([
        api.get("/holidays", { params: { start_date: start, end_date: end } }).catch(() => ({ data: [] })),
        api.get("/lesson-config/overrides").catch(() => ({ data: [] })),
        api.get("/practice-config/overrides").catch(() => ({ data: [] })),
        Promise.all(
          list.map((t) =>
            api.get(`/teachers/${t.id}/schedule-overrides`, { params: { start_date: start } })
              .then((r) => r.data.map((o) => ({ ...o, teacherName: t.name })))
              .catch(() => [])
          )
        )
      ]);
      setHolidays(holidayRes.data);
      setLessonOverrides(lessonRes.data);
      setPracticeOverrides(practiceRes.data);
      setTeacherOverrides(teacherOvLists.flat());
    } catch (err) {
      console.error("예외 캘린더 데이터 조회 실패:", err);
    }
  }, [month, teachers]);

  const fetchPreview = useCallback(async (date) => {
    if (!date) return;
    setPreviewLoading(true);
    try {
      const res = await api.get("/preview/date", { params: { date } });
      setPreview(res.data);
    } catch (err) {
      console.error("날짜 상태 조회 실패:", err);
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeachers().then((list) => fetchMonthData(list));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchMonthData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => {
    fetchPreview(selectedDate);
    closeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const refreshAll = async () => {
    await fetchMonthData();
    await fetchPreview(selectedDate);
  };

  // ── 날짜별 레이어 조회 ──
  const holidayOf = (d) => holidays.find((h) => h.date === d);
  const lessonOvOf = (d) => lessonOverrides.find((o) => o.date === d);
  const practiceOvOf = (d) => practiceOverrides.find((o) => o.date === d);
  const teacherOvsOf = (d) => teacherOverrides.filter((o) => o.date === d);

  // ── 폼 열기/닫기 ──
  function closeForm() {
    setActiveForm(null);
    setEditingId(null);
  }

  function openLessonForm(existing) {
    setEditingId(existing?.id || null);
    setLessonForm(existing ? {
      is_open: existing.is_open,
      start_time: existing.start_time ? existing.start_time.slice(0, 5) : "12:00",
      end_time: existing.end_time ? existing.end_time.slice(0, 5) : "18:00",
      reason: existing.reason || ""
    } : { is_open: true, start_time: "12:00", end_time: "18:00", reason: "" });
    setActiveForm("lesson");
  }

  function openPracticeForm(existing) {
    setEditingId(existing?.id || null);
    setPracticeForm(existing ? {
      is_open: existing.is_open,
      start_time: existing.start_time ? existing.start_time.slice(0, 5) : "12:00",
      end_time: existing.end_time ? existing.end_time.slice(0, 5) : "18:00",
      apply_unavailable_times: existing.apply_unavailable_times ?? true,
      reason: existing.reason || ""
    } : { is_open: true, start_time: "12:00", end_time: "18:00", apply_unavailable_times: true, reason: "" });
    setActiveForm("practice");
  }

  function openTeacherForm(existing) {
    setEditingId(existing?.id || null);
    setTeacherForm(existing ? {
      teacher_id: existing.teacher_id,
      type: existing.type,
      work_start: existing.work_start ? existing.work_start.slice(0, 5) : "09:00",
      work_end: existing.work_end ? existing.work_end.slice(0, 5) : "18:00",
      reason: existing.reason || ""
    } : { teacher_id: teachers[0]?.id || "", type: "OFF", work_start: "09:00", work_end: "18:00", reason: "" });
    setActiveForm("teacher");
  }

  function openHolidayForm() {
    setEditingId(null);
    setHolidayForm({ refund_tickets: true, reason: "" });
    setActiveForm("holiday");
  }

  // ── 저장/삭제 핸들러 ──
  async function saveLessonOverride() {
    setSaving(true);
    try {
      const payload = {
        date: selectedDate,
        is_open: lessonForm.is_open,
        start_time: lessonForm.is_open ? lessonForm.start_time : null,
        end_time: lessonForm.is_open ? lessonForm.end_time : null,
        reason: lessonForm.reason || null
      };
      if (editingId) {
        await api.put(`/lesson-config/overrides/${editingId}`, payload);
        toast("레슨 시간 변경이 수정되었습니다.");
      } else {
        await api.post("/lesson-config/overrides", payload);
        toast("레슨 시간 변경이 등록되었습니다.");
      }
      closeForm();
      refreshAll();
    } catch (err) {
      toast(err?.response?.data?.message || "저장 실패", "error");
    } finally {
      setSaving(false);
    }
  }

  async function deleteLessonOverride(id) {
    if (!(await confirm({ title: "레슨 시간 변경 삭제", message: "이 날짜의 레슨 시간 변경을 삭제하시겠습니까?", confirmText: "삭제", color: "error" }))) return;
    try {
      await api.delete(`/lesson-config/overrides/${id}`);
      toast("레슨 시간 변경이 삭제되었습니다.");
      refreshAll();
    } catch (err) {
      toast(err?.response?.data?.message || "삭제 실패", "error");
    }
  }

  async function savePracticeOverride() {
    setSaving(true);
    try {
      const payload = {
        date: selectedDate,
        is_open: practiceForm.is_open,
        start_time: practiceForm.is_open ? practiceForm.start_time : null,
        end_time: practiceForm.is_open ? practiceForm.end_time : null,
        apply_unavailable_times: practiceForm.apply_unavailable_times,
        reason: practiceForm.reason || null
      };
      if (editingId) {
        await api.put(`/practice-config/overrides/${editingId}`, payload);
        toast("연습실 시간 변경이 수정되었습니다.");
      } else {
        await api.post("/practice-config/overrides", payload);
        toast("연습실 시간 변경이 등록되었습니다.");
      }
      closeForm();
      refreshAll();
    } catch (err) {
      toast(err?.response?.data?.message || "저장 실패", "error");
    } finally {
      setSaving(false);
    }
  }

  async function deletePracticeOverride(id) {
    if (!(await confirm({ title: "연습실 시간 변경 삭제", message: "이 날짜의 연습실 시간 변경을 삭제하시겠습니까?", confirmText: "삭제", color: "error" }))) return;
    try {
      await api.delete(`/practice-config/overrides/${id}`);
      toast("연습실 시간 변경이 삭제되었습니다.");
      refreshAll();
    } catch (err) {
      toast(err?.response?.data?.message || "삭제 실패", "error");
    }
  }

  async function saveTeacherOverride() {
    if (!teacherForm.teacher_id) {
      toast("선생님을 선택해주세요.", "warning");
      return;
    }
    setSaving(true);
    try {
      let res;
      if (editingId) {
        res = await api.put(`/teachers/schedule-overrides/${editingId}`, {
          type: teacherForm.type,
          work_start: teacherForm.work_start,
          work_end: teacherForm.work_end,
          reason: teacherForm.reason
        });
      } else {
        res = await api.post(`/teachers/${teacherForm.teacher_id}/schedule-overrides`, {
          date: selectedDate,
          type: teacherForm.type,
          work_start: teacherForm.work_start,
          work_end: teacherForm.work_end,
          reason: teacherForm.reason
        });
      }
      alert({ title: editingId ? "변동 스케줄 수정 완료" : "변동 스케줄 등록 완료", message: res.data?.message || "저장되었습니다.", severity: "success" });
      closeForm();
      refreshAll();
    } catch (err) {
      alert({ title: "저장 실패", message: err?.response?.data?.message || "변동 스케줄 저장 실패", severity: "error" });
    } finally {
      setSaving(false);
    }
  }

  async function deleteTeacherOverride(id) {
    if (!(await confirm({ title: "변동 스케줄 삭제", message: "이 변동 스케줄을 삭제하시겠습니까?", confirmText: "삭제", color: "error" }))) return;
    try {
      const res = await api.delete(`/teachers/schedule-overrides/${id}`);
      toast(res.data?.message || "변동 스케줄이 삭제되었습니다.");
      refreshAll();
    } catch (err) {
      toast(err?.response?.data?.message || "삭제 실패: 해당 날짜에 레슨 예약이 있을 수 있습니다.", "error");
    }
  }

  async function saveHoliday() {
    const proceed = await confirm({
      title: "휴일 지정",
      message: `${selectedDate}을(를) 휴일로 지정합니다.\n해당 날짜의 레슨이 모두 삭제(백업)됩니다.${holidayForm.refund_tickets ? "\n회원에게 티켓이 반환됩니다." : "\n티켓은 반환되지 않습니다."}`,
      confirmText: "휴일 지정",
      color: "error"
    });
    if (!proceed) return;
    setSaving(true);
    try {
      const res = await api.post("/lessons/holiday", {
        date: selectedDate,
        refund_tickets: holidayForm.refund_tickets,
        reason: holidayForm.reason || "휴일"
      });
      alert({ title: "휴일 지정 완료", message: `${res.data?.message || ""}\n삭제된 레슨: ${res.data?.deletedCount ?? 0}건`, severity: "success" });
      closeForm();
      refreshAll();
    } catch (err) {
      toast(err?.response?.data?.message || "휴일 지정 실패", "error");
    } finally {
      setSaving(false);
    }
  }

  async function restoreHoliday(holiday) {
    const backupCount = holiday.backup_lessons?.length || 0;
    const proceed = await confirm({
      title: "휴일 지정 취소",
      message: `${holiday.date} 휴일 지정을 취소합니다.\n백업된 레슨 ${backupCount}건이 복구됩니다.${holiday.refund_tickets ? "\n반환됐던 티켓이 다시 차감됩니다." : ""}`,
      confirmText: "휴일 지정 취소",
      color: "primary"
    });
    if (!proceed) return;
    try {
      const res = await api.post("/lessons/restore-workday", { date: holiday.date });
      alert({ title: "업무일 복구 완료", message: `${res.data?.message || ""}\n복구된 레슨: ${res.data?.restoredCount ?? 0}건`, severity: "success" });
      refreshAll();
    } catch (err) {
      toast(err?.response?.data?.message || "복구 실패", "error");
    }
  }

  // ── 캘린더 셀 ──
  const firstDay = month.startOf("month");
  const startOffset = firstDay.day();
  const daysInMonth = month.daysInMonth();
  const todayStr = dayjs().format("YYYY-MM-DD");
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(firstDay.date(d).format("YYYY-MM-DD"));

  const selHoliday = holidayOf(selectedDate);
  const selLessonOv = lessonOvOf(selectedDate);
  const selPracticeOv = practiceOvOf(selectedDate);
  const selTeacherOvs = teacherOvsOf(selectedDate);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: "divider", minHeight: 48, flexShrink: 0, px: 1 }}>
        <Tab label="운영 시간 설정(기본)" sx={{ minHeight: 48 }} />
        <Tab label="운영 시간 설정(예외)" sx={{ minHeight: 48 }} />
        <Tab label="선생님 스케줄 설정(기본)" sx={{ minHeight: 48 }} />
        <Tab label="선생님 스케줄 설정(블록)" sx={{ minHeight: 48 }} />
      </Tabs>

      {subTab === 1 && (
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
        {/* 캘린더 */}
        <Box sx={{ flex: 1, minWidth: 0, p: 2, overflowY: "auto", borderRight: 1, borderColor: "divider" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6" fontWeight={600}>{month.format("YYYY년 M월")}</Typography>
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={() => setMonth(month.subtract(1, "month"))}><ChevronLeft /></IconButton>
              <Button size="small" onClick={() => { setMonth(dayjs().startOf("month")); setSelectedDate(todayStr); }}>오늘</Button>
              <IconButton size="small" onClick={() => setMonth(month.add(1, "month"))}><ChevronRight /></IconButton>
            </Stack>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 0.5 }}>
            {DAY_HEAD.map((d, i) => (
              <Typography key={d} variant="caption" textAlign="center"
                color={i === 0 ? "error.main" : i === 6 ? "primary.main" : "text.secondary"}>
                {d}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
            {cells.map((dateStr, idx) => {
              if (!dateStr) return <Box key={`empty-${idx}`} />;
              const holiday = holidayOf(dateStr);
              const lessonOv = lessonOvOf(dateStr);
              const practiceOv = practiceOvOf(dateStr);
              const tOvs = teacherOvsOf(dateStr);
              const isSelected = dateStr === selectedDate;
              const isToday = dateStr === todayStr;
              return (
                <Box
                  key={dateStr}
                  onClick={() => setSelectedDate(dateStr)}
                  sx={{
                    minHeight: 58,
                    p: 0.5,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    textAlign: "center",
                    border: "2px solid",
                    borderColor: isSelected ? "primary.main" : "transparent",
                    bgcolor: holiday
                      ? (darkMode ? "rgba(244, 67, 54, 0.16)" : "#fdecea")
                      : isSelected
                        ? "action.selected"
                        : "transparent",
                    "&:hover": { bgcolor: holiday ? (darkMode ? "rgba(244, 67, 54, 0.24)" : "#fbdcd9") : "action.hover" }
                  }}
                >
                  <Typography variant="body2" fontWeight={isToday || isSelected ? 700 : 400}
                    color={holiday ? layerColor("holiday") : "text.primary"}>
                    {Number(dateStr.slice(8, 10))}
                  </Typography>
                  <Box sx={{ mt: 0.3, minHeight: 10 }}>
                    {holiday && <LayerDot color={layerColor("holiday")} />}
                    {lessonOv && <LayerDot color={layerColor("lesson")} />}
                    {practiceOv && <LayerDot color={layerColor("practice")} />}
                    {tOvs.length > 0 && <LayerDot color={layerColor("teacher")} />}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Stack direction="row" spacing={2} mt={1.5} flexWrap="wrap" useFlexGap>
            {[["holiday", "휴일"], ["lesson", "레슨 시간 변경"], ["practice", "연습실 시간 변경"], ["teacher", "변동 스케줄"]].map(([key, label]) => (
              <Stack key={key} direction="row" alignItems="center" spacing={0.5}>
                <LayerDot color={layerColor(key)} />
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Stack>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            연습실 시간 변경은 오늘 이후 날짜만 표시됩니다.
          </Typography>
        </Box>

        {/* 날짜 상세 패널 */}
        <Box sx={{ width: 380, flexShrink: 0, p: 2, overflowY: "auto" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              {dayjs(selectedDate).format("M월 D일")} ({DAY_HEAD[dayjs(selectedDate).day()]})
            </Typography>
            <Tooltip title="새로고침"><IconButton size="small" onClick={refreshAll} aria-label="새로고침"><Refresh fontSize="small" /></IconButton></Tooltip>
          </Stack>

          {/* 유효 상태 요약 */}
          {previewLoading && !preview ? (
            <Box display="flex" alignItems="center" gap={1} py={1}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">상태 확인 중...</Typography>
            </Box>
          ) : preview ? (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                <Chip size="small" variant="outlined" color={preview.lesson.isOpen ? "default" : "error"}
                  label={preview.lesson.isOpen ? `레슨 ${preview.lesson.startTime || "-"}~${preview.lesson.endTime || "-"}` : "레슨 불가"} />
                <Chip size="small" variant="outlined" color={preview.practice.isOpen ? "default" : "error"}
                  label={preview.practice.isOpen ? `연습실 ${preview.practice.startTime || "-"}~${preview.practice.endTime || "-"}` : "연습실 휴무"} />
                <Chip size="small" variant="outlined" label={`근무 ${preview.workingTeacherCount}명`} />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                레슨 {preview.reservations.lessonCount}건
                {preview.reservations.blockCount > 0 && ` · 블록 ${preview.reservations.blockCount}건`}
                {` · 연습실 직접 예약 ${preview.reservations.manualPracticeCount}건`}
              </Typography>
              {preview.teachers?.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  근무: {preview.teachers.map((t) => `${t.name} ${t.windows.join(", ")}`).join(" / ")}
                </Typography>
              )}
            </Paper>
          ) : null}

          {/* 이 날짜의 예외 항목들 */}
          <Stack spacing={1} mb={1.5}>
            {selHoliday && (
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <EventBusy sx={{ fontSize: 18, color: layerColor("holiday") }} />
                    <Typography variant="body2" fontWeight={600} color={layerColor("holiday")}>
                      휴일 — {selHoliday.reason || "휴일"}
                    </Typography>
                  </Stack>
                  <Button size="small" onClick={() => restoreHoliday(selHoliday)}>지정 취소</Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  백업 레슨 {selHoliday.backup_lessons?.length || 0}건 · {selHoliday.refund_tickets ? "티켓 반환됨" : "티켓 반환 안 함"}
                </Typography>
              </Paper>
            )}

            {selLessonOv && (
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <LayerDot color={layerColor("lesson")} />
                    <Typography variant="body2" fontWeight={600}>
                      레슨 시간 변경 — {selLessonOv.is_open
                        ? (selLessonOv.start_time ? `${selLessonOv.start_time.slice(0, 5)}~${selLessonOv.end_time?.slice(0, 5)}` : "기본값 적용")
                        : "레슨 불가"}
                    </Typography>
                  </Stack>
                  <Box>
                    <IconButton size="small" onClick={() => openLessonForm(selLessonOv)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => deleteLessonOverride(selLessonOv.id)}><Delete fontSize="small" /></IconButton>
                  </Box>
                </Stack>
                {selLessonOv.reason && <Typography variant="caption" color="text.secondary">사유: {selLessonOv.reason}</Typography>}
              </Paper>
            )}

            {selPracticeOv && (
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <LayerDot color={layerColor("practice")} />
                    <Typography variant="body2" fontWeight={600}>
                      연습실 시간 변경 — {selPracticeOv.is_open
                        ? `${selPracticeOv.start_time?.slice(0, 5) || "-"}~${selPracticeOv.end_time?.slice(0, 5) || "-"}`
                        : "휴무"}
                    </Typography>
                  </Stack>
                  <Box>
                    <IconButton size="small" onClick={() => openPracticeForm(selPracticeOv)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => deletePracticeOverride(selPracticeOv.id)}><Delete fontSize="small" /></IconButton>
                  </Box>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {selPracticeOv.apply_unavailable_times === false && "차단 시간 해제 · "}
                  {selPracticeOv.reason ? `사유: ${selPracticeOv.reason}` : ""}
                </Typography>
              </Paper>
            )}

            {selTeacherOvs.map((o) => (
              <Paper key={o.id} variant="outlined" sx={{ p: 1.2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <LayerDot color={layerColor("teacher")} />
                    <Typography variant="body2" fontWeight={600}>
                      {o.teacherName} — {o.type === "OFF" ? "휴무" : `${o.work_start?.slice(0, 5)}~${o.work_end?.slice(0, 5)} 근무`}
                    </Typography>
                  </Stack>
                  <Box>
                    <IconButton size="small" onClick={() => openTeacherForm(o)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => deleteTeacherOverride(o.id)}><Delete fontSize="small" /></IconButton>
                  </Box>
                </Stack>
                {o.reason && <Typography variant="caption" color="text.secondary">사유: {o.reason}</Typography>}
              </Paper>
            ))}

            {!selHoliday && !selLessonOv && !selPracticeOv && selTeacherOvs.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", py: 1 }}>
                이 날짜에 등록된 항목이 없습니다.
              </Typography>
            )}
          </Stack>

          {/* 추가 액션 */}
          {!activeForm && (
            <Stack spacing={0.8}>
              {!selLessonOv && (
                <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => openLessonForm(null)}>레슨 시간 변경</Button>
              )}
              {!selPracticeOv && (
                <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => openPracticeForm(null)}>연습실 시간 변경</Button>
              )}
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => openTeacherForm(null)}>변동 스케줄 추가</Button>
              {!selHoliday && (
                <Button size="small" variant="outlined" color="error" startIcon={<EventBusy />} onClick={openHolidayForm}>휴일로 지정</Button>
              )}
            </Stack>
          )}

          {/* 인라인 폼 */}
          {activeForm && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {activeForm === "lesson" && (editingId ? "레슨 시간 변경 수정" : "레슨 시간 변경")}
                  {activeForm === "practice" && (editingId ? "연습실 시간 변경 수정" : "연습실 시간 변경")}
                  {activeForm === "teacher" && (editingId ? "변동 스케줄 수정" : "변동 스케줄 추가")}
                  {activeForm === "holiday" && "휴일 지정"}
                </Typography>
                <IconButton size="small" onClick={closeForm}><Close fontSize="small" /></IconButton>
              </Stack>

              {activeForm === "lesson" && (
                <Stack spacing={1.2}>
                  <FormControlLabel
                    control={<Switch size="small" checked={lessonForm.is_open}
                      onChange={(e) => setLessonForm({ ...lessonForm, is_open: e.target.checked })} />}
                    label={<Typography variant="body2">레슨 가능</Typography>}
                  />
                  {lessonForm.is_open && (
                    <Stack direction="row" spacing={1}>
                      <TimeSelect label="시작" value={lessonForm.start_time} onChange={(v) => setLessonForm({ ...lessonForm, start_time: v })} />
                      <TimeSelect label="종료" value={lessonForm.end_time} onChange={(v) => setLessonForm({ ...lessonForm, end_time: v })} />
                    </Stack>
                  )}
                  <TextField size="small" label="사유" value={lessonForm.reason}
                    onChange={(e) => setLessonForm({ ...lessonForm, reason: e.target.value })} />
                  <DateImpactPreview
                    date={selectedDate}
                    change={{
                      kind: "LESSON_OVERRIDE",
                      payload: {
                        is_open: lessonForm.is_open,
                        start_time: lessonForm.is_open ? lessonForm.start_time : null,
                        end_time: lessonForm.is_open ? lessonForm.end_time : null
                      }
                    }}
                  />
                  <Button variant="contained" size="small" onClick={saveLessonOverride} disabled={saving}>
                    {saving ? "저장 중..." : "저장"}
                  </Button>
                </Stack>
              )}

              {activeForm === "practice" && (
                <Stack spacing={1.2}>
                  <FormControl size="small">
                    <InputLabel>유형</InputLabel>
                    <Select value={practiceForm.is_open ? "open" : "closed"} label="유형"
                      onChange={(e) => setPracticeForm({ ...practiceForm, is_open: e.target.value === "open" })}>
                      <MenuItem value="open">운영 시간 변경</MenuItem>
                      <MenuItem value="closed">휴무</MenuItem>
                    </Select>
                  </FormControl>
                  {practiceForm.is_open && (
                    <>
                      <Stack direction="row" spacing={1}>
                        <TimeSelect label="시작" value={practiceForm.start_time} onChange={(v) => setPracticeForm({ ...practiceForm, start_time: v })} />
                        <TimeSelect label="종료" value={practiceForm.end_time} onChange={(v) => setPracticeForm({ ...practiceForm, end_time: v })} />
                      </Stack>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={practiceForm.apply_unavailable_times}
                          onChange={(e) => setPracticeForm({ ...practiceForm, apply_unavailable_times: e.target.checked })} />}
                        label={<Typography variant="body2">사용 불가능한 시간대 제한 적용</Typography>}
                      />
                    </>
                  )}
                  <TextField size="small" label="사유" value={practiceForm.reason}
                    onChange={(e) => setPracticeForm({ ...practiceForm, reason: e.target.value })} />
                  <Typography variant="caption" color="text.secondary">
                    당일 예약 제한 등 세부 옵션은 연습실 페이지의 예약 설정에서 수정할 수 있습니다.
                  </Typography>
                  <DateImpactPreview
                    date={selectedDate}
                    change={{
                      kind: "PRACTICE_OVERRIDE",
                      payload: {
                        is_open: practiceForm.is_open,
                        start_time: practiceForm.is_open ? practiceForm.start_time : null,
                        end_time: practiceForm.is_open ? practiceForm.end_time : null,
                        apply_unavailable_times: practiceForm.apply_unavailable_times
                      }
                    }}
                  />
                  <Button variant="contained" size="small" onClick={savePracticeOverride} disabled={saving}>
                    {saving ? "저장 중..." : "저장"}
                  </Button>
                </Stack>
              )}

              {activeForm === "teacher" && (
                <Stack spacing={1.2}>
                  <FormControl size="small" disabled={!!editingId}>
                    <InputLabel>선생님</InputLabel>
                    <Select value={teacherForm.teacher_id} label="선생님"
                      onChange={(e) => setTeacherForm({ ...teacherForm, teacher_id: e.target.value })}>
                      {teachers.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small">
                    <InputLabel>변동 유형</InputLabel>
                    <Select value={teacherForm.type} label="변동 유형"
                      onChange={(e) => setTeacherForm({ ...teacherForm, type: e.target.value })}>
                      <MenuItem value="OFF">휴가</MenuItem>
                      <MenuItem value="ADJUSTED">근무 시간 조정</MenuItem>
                    </Select>
                  </FormControl>
                  {teacherForm.type === "ADJUSTED" && (
                    <Stack direction="row" spacing={1}>
                      <TimeSelect label="시작" value={teacherForm.work_start} onChange={(v) => setTeacherForm({ ...teacherForm, work_start: v })} />
                      <TimeSelect label="종료" value={teacherForm.work_end} onChange={(v) => setTeacherForm({ ...teacherForm, work_end: v })} />
                    </Stack>
                  )}
                  <TextField size="small" label="사유 (선택)" value={teacherForm.reason}
                    onChange={(e) => setTeacherForm({ ...teacherForm, reason: e.target.value })} />
                  {teacherForm.teacher_id && (
                    <DateImpactPreview
                      date={selectedDate}
                      change={{
                        kind: "TEACHER_OVERRIDE",
                        payload: {
                          teacher_id: teacherForm.teacher_id,
                          type: teacherForm.type,
                          work_start: teacherForm.work_start,
                          work_end: teacherForm.work_end
                        }
                      }}
                    />
                  )}
                  <Button variant="contained" size="small" onClick={saveTeacherOverride} disabled={saving}>
                    {saving ? "저장 중..." : "저장"}
                  </Button>
                </Stack>
              )}

              {activeForm === "holiday" && (
                <Stack spacing={1.2}>
                  <TextField size="small" label="사유" placeholder="예: 명절 휴무" value={holidayForm.reason}
                    onChange={(e) => setHolidayForm({ ...holidayForm, reason: e.target.value })} />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={holidayForm.refund_tickets}
                      onChange={(e) => setHolidayForm({ ...holidayForm, refund_tickets: e.target.checked })} />}
                    label={<Typography variant="body2">회원에게 레슨 티켓 반환</Typography>}
                  />
                  <DateImpactPreview date={selectedDate} change={{ kind: "HOLIDAY" }} />
                  <Button variant="contained" color="error" size="small" onClick={saveHoliday} disabled={saving}>
                    {saving ? "처리 중..." : "휴일 지정"}
                  </Button>
                </Stack>
              )}
            </Paper>
          )}
        </Box>
      </Box>
      )}

      {subTab === 0 && <OperationsBasicHours />}
      {subTab === 2 && <OperationsTeacherSchedule />}
      {subTab === 3 && <OperationsBlockSchedule />}
    </Box>
  );
}
